import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  Dialog,
  Field,
  FormError,
  IconAlertOctagon,
  IconBuilding,
  Input,
  NumberInput,
  Textarea,
  useToast,
} from '@/design-system';
import { setAccessToken, toUserMessage, vendorAccessApi } from '@/lib/api';
import { useAuth } from '@/providers/auth-provider';

/**
 * Seletor de organização — exclusivo do `PLATFORM_ADMIN` (o fornecedor).
 *
 * Não é um "trocar de empresa" trivial: cada troca é uma ELEVAÇÃO DE ACESSO
 * auditada (plano §6.5). O fluxo exige:
 *   · justificativa de no mínimo 20 caracteres, gravada no grant;
 *   · duração explícita, limitada pelo servidor;
 *   · confirmação de que o operador entendeu que entrará em ambiente de cliente
 *     auditado.
 *
 * O token elevado NÃO tem refresh: ele expira e pronto. Isso é deliberado — um
 * acesso de fornecedor não deve se renovar sozinho enquanto a aba fica aberta.
 *
 * ACESSIBILIDADE: o diálogo tem `title` e `description`, o aviso de risco é
 * texto com ícone (não só a cor amarela), e o botão de confirmação diz o que
 * vai acontecer ("Entrar como fornecedor") em vez de um "OK" genérico.
 */
const elevationSchema = z.object({
  organizationId: z.string().uuid('Informe um identificador de organização válido.'),
  justification: z
    .string()
    .trim()
    .min(20, 'A justificativa precisa ter ao menos 20 caracteres.')
    .max(1000),
  minutes: z.coerce.number().int().min(5).max(480),
});

type ElevationValues = z.infer<typeof elevationSchema>;

export function OrganizationSwitcher() {
  const [open, setOpen] = useState(false);
  const { organization, refreshProfile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<ElevationValues>({
    resolver: zodResolver(elevationSchema),
    defaultValues: { organizationId: '', justification: '', minutes: 30 },
  });

  const elevate = useMutation({
    mutationFn: (values: ElevationValues) => vendorAccessApi.elevate(values),
    onError: (error) => {
      toast({
        tone: 'error',
        title: 'Não foi possível elevar o acesso',
        description: toUserMessage(error),
      });
    },
    onSuccess: async (result) => {
      // O token elevado substitui o da sessão — em memória, como todos os outros.
      setAccessToken(result.accessToken, result.accessTokenExpiresAt);
      queryClient.clear();
      await refreshProfile();
      setOpen(false);
      form.reset();
      toast({
        tone: 'warning',
        title: 'Acesso de fornecedor ativo',
        description:
          'Você está no ambiente de uma organização cliente. Todas as ações são auditadas como acesso do fornecedor.',
        duration: 0,
      });
    },
  });

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        leadingIcon={<IconBuilding size={15} />}
        onClick={() => setOpen(true)}
      >
        <span className="hidden max-w-[10rem] truncate md:inline">
          {organization?.name ?? 'Selecionar organização'}
        </span>
        <span className="md:hidden">Organização</span>
      </Button>

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Acessar organização cliente"
        description="Esta operação eleva seu acesso a uma organização de cliente e é registrada na trilha de auditoria dela."
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={elevate.isPending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={elevate.isPending}
              onClick={() => void form.handleSubmit((values) => elevate.mutate(values))()}
            >
              Entrar como fornecedor
            </Button>
          </>
        }
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((values) => elevate.mutate(values))();
          }}
          noValidate
        >
          <p className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle px-3 py-2.5 text-sm text-warning-content">
            <IconAlertOctagon size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              <strong className="font-semibold">Ambiente de cliente auditado.</strong> Cada ação sua
              gera um evento <span className="font-mono text-xs">VENDOR_ACCESS</span> na trilha do
              cliente, com a justificativa informada abaixo. A trilha é imutável.
            </span>
          </p>

          {elevate.isError ? <FormError>{toUserMessage(elevate.error)}</FormError> : null}

          <Field
            label="Identificador da organização"
            required
            error={form.formState.errors.organizationId?.message}
            description="UUID da organização de destino."
          >
            <Input {...form.register('organizationId')} autoComplete="off" spellCheck={false} />
          </Field>

          <Field
            label="Justificativa do acesso"
            required
            error={form.formState.errors.justification?.message}
            description="Mínimo de 20 caracteres. Fica registrada no grant e é lida pelo cliente na trilha."
          >
            <Textarea
              {...form.register('justification')}
              rows={3}
              maxLength={1000}
              showCounter
              value={form.watch('justification')}
              placeholder="Ex.: chamado 4821 — investigar falha de aprovação relatada pelo cliente."
            />
          </Field>

          <Field
            label="Duração do acesso (minutos)"
            required
            error={form.formState.errors.minutes?.message}
            description="Entre 5 e 480 minutos. O servidor pode reduzir conforme a política da plataforma."
          >
            <NumberInput {...form.register('minutes')} min={5} max={480} className="w-32" />
          </Field>
        </form>
      </Dialog>
    </>
  );
}
